/**
 * Builds the full PDF content structure from the raw API response.
 *
 * @param {Object} content - Raw plan content from the API
 * @param {Object} deps - Reactive dependencies
 * @param {import('vue').Ref} deps.pdfData - PDF metadata ref (annexures, signatures, etc.)
 * @param {import('vue').Ref} deps.settingsDictionary - Settings key/value map ref
 * @param {import('vue').Ref<Array>} deps.figuresList - Figure index ref (passed by reference for reactivity)
 * @param {import('vue').Ref<Map>} deps.frameworksLookup - Framework ID-to-name map ref
 * @param {import('vue').Ref<Map>} deps.goalsLookup - Goal ID-to-name map ref
 * @param {import('vue').ComputedRef<string>} deps.municipalitySettingsPrefix - 'district' or 'municipality'
 */
export function buildPdfContent(content, { pdfData, settingsDictionary, figuresList, frameworksLookup, goalsLookup, municipalitySettingsPrefix }) {
  const devStrategyMap = new Map();
  if (Array.isArray(content.developmentStrategies)) {
    content.developmentStrategies.forEach(ds => {
      devStrategyMap.set(ds.id, ds.name);
    });
  }

  const generatePowerBiContent = (profileData, settingsKey) => {
    if (!profileData?.loadReportFromStatHub) return [];

    const rawValue = settingsDictionary.value[settingsKey];
    if (!rawValue) return [];

    return rawValue
      .split(',')
      .map(id => id.trim())
      .filter(Boolean)
      .map(pageId => ({ type: 'powerbi', pageId }));
  };

  // Resolves the annexure IDs attached to a single content sub-section into link
  // items, so document links render beneath the section they belong to.
  const getDocumentsFor = (subSection) => {
    const ids = subSection?.documents || [];
    if (!ids.length) return [];

    return (pdfData.value.annexures || [])
      .filter(a => ids.includes(a.id))
      .map(a => ({ type: 'link', label: a.name, url: a.document?.url }));
  };

  const prefix = municipalitySettingsPrefix.value;

  return [
    {
      id: 'preliminaries',
      label: 'Preliminaries',
      isCategory: true,
      breakBefore: true,
      subsections: [
        {
          id: 'list-of-abbreviations',
          label: 'List of abbreviations',
          content: [
            content.preliminaries.abbreviations?.length
              ? { type: 'table', rows: content.preliminaries.abbreviations }
              : { type: 'empty' },
          ],
        },
        {
          id: 'list-of-figures',
          label: 'List of figures',
          breakBefore: true,
          content: [
            { type: 'table', rows: figuresList },
          ],
        },
        {
          id: 'message-from-the-mayor',
          label: 'Message from the Mayor',
          breakBefore: true,
          content: [
            { type: 'tinymce', value: content.preliminaries?.mayor?.message },
            {
              type: 'official',
              name: content.preliminaries?.mayor?.name,
              title: content.preliminaries?.mayor?.title,
              signature: pdfData.value?.mayorSignature,
              photo: pdfData.value?.mayorPhoto,
            },
          ],
        },
        {
          id: 'message-from-the-munical-manager',
          label: 'Message from the Municipal Manager',
          breakBefore: true,
          content: [
            { type: 'tinymce', value: content.preliminaries?.municipalManager?.message },
            {
              type: 'official',
              name: content.preliminaries?.municipalManager?.name,
              title: content.preliminaries?.municipalManager?.title,
              signature: pdfData.value?.municipalManagerSignature,
              photo: pdfData.value?.municipalManagerPhoto,
            },
          ],
        },
      ],
    },
    {
      id: 'executive-summary',
      label: 'Executive Summary',
      breakBefore: true,
      content: [
        { type: 'heading', value: 'Who we are' },
        { type: 'tinymce', value: content.executiveSummary?.whoWeAre },
        { type: 'heading', value: 'The issues we\'re facing' },
        { type: 'tinymce', value: content.executiveSummary?.issues },
        { type: 'heading', value: 'Opportunities we can offer' },
        { type: 'tinymce', value: content.executiveSummary?.opportunitiesOffered },
        { type: 'heading', value: 'Our 5-year service delivery plan' },
        { type: 'tinymce', value: content.executiveSummary?.serviceDeliveryImprovement },
        { type: 'heading', value: 'Priorities of council' },
        { type: 'tinymce', value: content.executiveSummary?.councilPriorities },
        { type: 'heading', value: 'What you can expect from us' },
        { type: 'tinymce', value: content.executiveSummary?.expectations },
        { type: 'heading', value: 'How we\'ll be measured' },
        { type: 'tinymce', value: content.executiveSummary?.progressMeasurement },
        { type: 'heading', value: 'How this IDP was developed' },
        { type: 'tinymce', value: content.executiveSummary?.howWasIdpDeveloped },
      ],
    },
    {
      id: 'introduction',
      label: 'Introduction',
      breakBefore: true,
      content: [
        { type: 'tinymce', value: content.introduction?.introduction },
        { type: 'heading', value: 'Powers and functions' },
        { type: 'tinymce', value: content.introduction?.powersAndFunctions },
      ],
    },
    {
      id: 'vision-mission-values-goal-objectives',
      label: 'Vision, Mission, Values, Goals and Objectives',
      breakBefore: true,
      content: [
        { type: 'heading', value: 'Vision' },
        { type: 'tinymce', value: content.vision?.vision },
        { type: 'heading', value: 'Mission' },
        { type: 'tinymce', value: content.vision?.mission },
        { type: 'heading', value: 'Values' },
        { type: 'tinymce', value: content.vision?.values },
        { type: 'heading', value: 'Goals' },
        { type: 'tinymce', value: content.vision?.goals },
        { type: 'heading', value: 'Objectives' },
        { type: 'tinymce', value: content.vision?.objectives },
      ],
    },
    {
      id: 'situational-analysis',
      label: 'Situational Analysis',
      breakBefore: true,
      content: [
        { type: 'heading', value: 'Overview of the municipality' },
        { type: 'tinymce', value: content.situationalAnalysis?.overview?.text },
        ...getDocumentsFor(content.situationalAnalysis?.overview),
      ],
      subsections: [
        {
          id: 'demographic-profile',
          label: 'Demographic profile',
          breakBefore: true,
          content: [
            ...generatePowerBiContent(content.situationalAnalysis?.demographicProfile, `${prefix}-powerbi-pageids-demographic`),
            { type: 'tinymce', value: content.situationalAnalysis?.demographicProfile?.text },
            ...getDocumentsFor(content.situationalAnalysis?.demographicProfile),
          ].filter(Boolean),
        },
        {
          id: 'socio-economic-profile',
          label: 'Socio-economic profile',
          breakBefore: true,
          content: [
            ...generatePowerBiContent(content.situationalAnalysis?.socioeconomicProfile, `${prefix}-powerbi-pageids-socio-economic`),
            { type: 'tinymce', value: content.situationalAnalysis?.socioeconomicProfile?.text },
            ...getDocumentsFor(content.situationalAnalysis?.socioeconomicProfile),
          ].filter(Boolean),
        },
        {
          id: 'economic-profile',
          label: 'Economic profile',
          breakBefore: true,
          content: [
            ...generatePowerBiContent(content.situationalAnalysis?.economicProfile, `${prefix}-powerbi-pageids-economic`),
            { type: 'tinymce', value: content.situationalAnalysis?.economicProfile?.text },
            ...getDocumentsFor(content.situationalAnalysis?.economicProfile),
          ].filter(Boolean),
        },
        {
          id: 'service-delivery',
          label: 'Service delivery and infrastructure',
          breakBefore: true,
          content: [
            ...generatePowerBiContent(content.situationalAnalysis?.serviceDeliveryAndInfrastructure, `${prefix}-powerbi-pageids-service-delivery-and-infrastructure`),
            { type: 'tinymce', value: content.situationalAnalysis?.serviceDeliveryAndInfrastructure?.text },
            ...getDocumentsFor(content.situationalAnalysis?.serviceDeliveryAndInfrastructure),
          ].filter(Boolean),
        },
        {
          id: 'finance-and-governance',
          label: 'Finance and governance',
          breakBefore: true,
          content: [
            ...generatePowerBiContent(content.situationalAnalysis?.financeAndGovernance, `${prefix}-powerbi-pageids-finance-and-governance`),
            { type: 'tinymce', value: content.situationalAnalysis?.financeAndGovernance?.text },
            ...getDocumentsFor(content.situationalAnalysis?.financeAndGovernance),
          ].filter(Boolean),
        },
        {
          id: 'environmental-context',
          label: 'Environmental context',
          breakBefore: true,
          content: [
            { type: 'tinymce', value: content.situationalAnalysis?.environmentalContext.text },
            ...getDocumentsFor(content.situationalAnalysis?.environmentalContext),
          ],
        },
        {
          id: 'overview-of-community-needs-and-ward-priorities',
          label: 'Overview of community needs and ward priorities',
          breakBefore: true,
          content: [
            { type: 'tinymce', value: content.situationalAnalysis?.communityNeedsOverview.text },
            ...getDocumentsFor(content.situationalAnalysis?.communityNeedsOverview),
          ],
        },
      ],
    },
    {
      id: 'institutional-framework',
      label: 'Institutional Framework',
      breakBefore: true,
      content: [
        { type: 'heading', value: 'Governance' },
        { type: 'tinymce', value: content.institutionalFramework?.governance.text },
        ...getDocumentsFor(content.institutionalFramework?.governance),
        { type: 'heading', value: 'Municipal structure' },
        { type: 'tinymce', value: content.institutionalFramework?.municipalStructure.text },
        ...getDocumentsFor(content.institutionalFramework?.municipalStructure),
        { type: 'heading', value: 'Skills requirements' },
        { type: 'tinymce', value: content.institutionalFramework?.skillsRequirements.text },
        ...getDocumentsFor(content.institutionalFramework?.skillsRequirements),
        { type: 'heading', value: 'Strengths and weaknesses' },
        { type: 'tinymce', value: content.institutionalFramework?.strengthsAndWeaknesses.text },
        ...getDocumentsFor(content.institutionalFramework?.strengthsAndWeaknesses),
        { type: 'heading', value: 'Employment equity' },
        { type: 'tinymce', value: content.institutionalFramework?.employmentEquity.text },
        ...getDocumentsFor(content.institutionalFramework?.employmentEquity),
      ],
    },
    {
      id: 'spatial-development-framework',
      label: 'Spatial Development Framework',
      breakBefore: true,
      content: [
        { type: 'heading', value: 'Spatial, economic and development rationale' },
        { type: 'tinymce', value: content.spatialDevelopmentFramework?.spatialEconomicAndDevelopmentRationale.text },
        ...getDocumentsFor(content.spatialDevelopmentFramework?.spatialEconomicAndDevelopmentRationale),
        { type: 'heading', value: 'Desired spatial outcome' },
        { type: 'tinymce', value: content.spatialDevelopmentFramework?.desiredSpatialOutcome.text },
        ...getDocumentsFor(content.spatialDevelopmentFramework?.desiredSpatialOutcome),
        { type: 'heading', value: 'Spatial strategies' },
        { type: 'tinymce', value: content.spatialDevelopmentFramework?.spatialStrategies.text },
        ...getDocumentsFor(content.spatialDevelopmentFramework?.spatialStrategies),
      ],
    },
    {
      id: 'development-strategies',
      label: 'Development Strategies',
      breakBefore: true,
      content: !content?.developmentStrategies?.length ? [{ type: 'empty' }] : content.developmentStrategies.flatMap(ds => {
        const rows = (ds.frameworkAlignments || []).map(fa => {
          const framework = frameworksLookup.value.get(fa.frameworkId);

          const goals = (fa.goals || []).flatMap(g => {
            const goal = goalsLookup.value.get(g.id);
            const subgoals = (g.subgoalIds || []).map(subId => goalsLookup.value.get(subId)?.name);
            return [goal?.name, ...subgoals];
          });

          return { framework, goals };
        });

        const output = [
          { type: 'heading', value: ds.name },
          { type: 'tinymce', value: ds.description },
        ];
        if (rows.length > 0) {
          output.push({ type: 'table', rows });
        }

        return output;
      }),
    },
    {
      id: 'implementation-plan',
      label: 'Implementation Plan',
      breakBefore: true,
      content: [
        { type: 'heading', value: 'Overview of implementation plan' },
        { type: 'tinymce', value: content.implementationPlan?.overview.text },
        ...getDocumentsFor(content.implementationPlan?.overview),
      ],
      subsections: [
        {
          id: 'municipal-programmes-and-projects',
          label: 'Municipal programmes and projects',
          content: content.implementationPlan?.municipalProgrammesAndProjects?.length ? undefined : [{ type: 'empty' }],
          subsections: (content.implementationPlan?.municipalProgrammesAndProjects || []).map((program, idx) => ({
            id: `proj-sec-${idx}`,
            label: program.sectionName,
            content: (program.projects || []).length === 0 ? [{ type: 'empty' }] : [
              {
                type: 'table',
                variant: 'projects',
                rows: program.projects.map(project => ({
                  description: project.description,
                  strategy: devStrategyMap.get(project.developmentStrategyId) || 'N/A',
                  wardLocation: project.wardLocation,
                  totalProjectValue: project.totalProjectValue,
                  budget: project.budget?.capital || {},
                })),
              },
            ],
          })),
        },
        {
          id: 'programmes-and-projects-of-other-organs-of-state',
          label: 'Programmes and projects of other organs of state',
          // The project sections above no longer each fill a page, so this needs
          // to ask for the page break it used to get for free
          breakBefore: true,
          content: [
            { type: 'tinymce', value: content.implementationPlan?.otherProgrammesAndProjects.text },
            ...getDocumentsFor(content.implementationPlan?.otherProgrammesAndProjects),
          ],
        },
        {
          id: 'local-economic-development',
          label: 'Local Economic Development',
          content: [
            { type: 'tinymce', value: content.implementationPlan?.localEconomicDevelopment.text },
            ...getDocumentsFor(content.implementationPlan?.localEconomicDevelopment),
          ],
        },
        {
          id: 'disaster-management-plan',
          label: 'Disaster management plan',
          content: [
            { type: 'tinymce', value: content.implementationPlan?.disasterManagementPlan.text },
            ...getDocumentsFor(content.implementationPlan?.disasterManagementPlan),
          ],
        },
        {
          id: 'climate-change-response-implementation-plan',
          label: 'Climate change response implementation plan',
          content: [
            { type: 'tinymce', value: content.implementationPlan?.climateChangeResponse.text },
            ...getDocumentsFor(content.implementationPlan?.climateChangeResponse),
          ],
        },
      ],
    },
    {
      id: 'financial-plan',
      label: 'Financial Plan',
      breakBefore: true,
      content: [
        { type: 'heading', value: 'Financial strategy' },
        { type: 'tinymce', value: content.financialPlan?.strategy.text },
        ...getDocumentsFor(content.financialPlan?.strategy),
      ],
      subsections: [
        {
          id: 'budget-per-development-strategy',
          label: 'Summary of expenditure by development strategy - operating budget',
          breakBefore: true,
          content: [devStrategyMap.size ? { type: 'table', budgetType: 'operating' } : { type: 'empty' }],
        },
        {
          id: 'capital-budget',
          label: 'Summary of expenditure by development strategy - capital budget',
          breakBefore: true,
          content: [devStrategyMap.size ? { type: 'table', budgetType: 'capital' } : { type: 'empty' }],
        },
        {
          id: 'projection-of-financial-performance',
          label: 'Projection of financial performance',
          breakBefore: true,
          content: [{ type: 'table', storeKey: 'financialPerformanceProjection' }],
        },
        {
          id: 'capital-expenditure-and-financing',
          label: 'Capital expenditure and financing',
          breakBefore: true,
          content: [{ type: 'table', storeKey: 'capitalExpenditureAndFinancing' }],
        },
        {
          id: 'projection-of-financial-position',
          label: 'Projection of financial position',
          breakBefore: true,
          content: [{ type: 'table', storeKey: 'financialPositionProjection' }],
        },
        {
          id: 'projection-of-cash-flow',
          label: 'Projection of cash flow',
          breakBefore: true,
          content: [{ type: 'table', storeKey: 'cashFlowProjection' }],
        },
        {
          id: 'comments-to-the-financial-plan',
          label: 'Comments to the financial plan',
          breakBefore: true,
          content: [
            { type: 'tinymce', value: content.financialPlan?.comments.text },
            ...getDocumentsFor(content.financialPlan?.comments),
          ],
        },
      ],
    },
    {
      id: 'performance-monitoring-framework',
      label: 'Performance Monitoring Framework',
      breakBefore: true,
      content: [
        { type: 'heading', value: 'Introduction to performance indicators' },
        { type: 'tinymce', value: content.keyPerformanceIndicators?.introduction.text },
        ...getDocumentsFor(content.keyPerformanceIndicators?.introduction),
      ],
      subsections: [
        {
          id: 'performance-targets',
          label: 'Performance targets',
          breakBefore: true,
          content: (() => {
            if (!content.keyPerformanceIndicators?.performanceTargets?.length) return [{ type: 'empty' }];

            const groupedTargets = {};
            (content.keyPerformanceIndicators?.performanceTargets || []).forEach(target => {
              const strategyId = target.developmentStrategyId;
              if (!groupedTargets[strategyId]) {
                groupedTargets[strategyId] = [];
              }
              groupedTargets[strategyId].push(target);
            });

            return Object.entries(groupedTargets).flatMap(([strategyId, targets]) => [
              { type: 'heading', value: devStrategyMap.get(parseInt(strategyId)) },
              {
                type: 'table',
                rows: targets.map(target => ({
                  'indicator': target.name,
                  'description': target.description,
                  ...target.indicatorValues,
                })),
              },
            ]);
          })(),
        },
      ],
    },
    {
      id: 'annexures',
      label: 'Annexures',
      breakBefore: true,
      content: [
        pdfData.value.annexures.length
          ? pdfData.value.annexures.map(a => ({
            type: 'link',
            label: a.name,
            url: a.document?.url,
          }))
          : { type: 'empty' },
      ],
    },
  ];
}

/**
 * Flattens a nested sections tree into a single array, processing TinyMCE content
 * along the way (heading demotion, table-to-div conversion, figure indexing).
 *
 * @param {Array} sections - Section tree nodes
 * @param {import('vue').Ref<Array>} figuresList - Reactive ref accumulating figure metadata
 * @param {number} level - Current nesting depth
 * @param {Array} targetArray - Accumulator for flattened sections
 * @param {string|null} rootId - ID of the top-level ancestor section
 * @param {Object|null} parent - Parent section metadata
 */
export function flattenSections(sections, figuresList, level = 0, targetArray = [], rootId = null, parent = null) {
  sections.forEach((section, index) => {
    if (section.content) {
      section.content.forEach(item => {
        // A cleared editor still holds markup such as <p>&nbsp;</p>, so judge it by
        // its text and media, and render the placeholder line in its place
        if (item.type === 'tinymce') {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = item.value || '';

          const hasText = tempDiv.textContent.replace(/​/g, '').trim() !== '';
          const hasMedia = tempDiv.querySelector('img, table, figure, iframe, video');
          if (!hasText && !hasMedia) {
            item.type = 'empty';
          }
        }

        if (item.type === 'tinymce' && item.value) {
          const tempDiv = document.createElement('div');

          // Strip Zero-Width Spaces (​) — safe in TinyMCE but cause fatal infinite loops in Paged.js
          const cleanValue = item.value.replace(/\u200B/g, '');
          tempDiv.innerHTML = cleanValue;

          // Demote h1->h3, h2->h4, h3->h5 to avoid colliding with structural CSS
          const editorHeadings = tempDiv.querySelectorAll('h1, h2, h3');

          Array.from(editorHeadings).reverse().forEach((h) => {
            const hLevel = parseInt(h.tagName.substring(1));
            const newTag = `h${hLevel + 2}`;
            const newH = document.createElement(newTag);

            newH.innerHTML = h.innerHTML;
            for (const attr of h.attributes) {
              newH.setAttribute(attr.name, attr.value);
            }
            h.parentNode.replaceChild(newH, h);
          });

          // Convert table elements to divs — Paged.js handles divs more reliably
          const tableElements = tempDiv.querySelectorAll('table, thead, tbody, tfoot, tr, th, td');

          Array.from(tableElements).reverse().forEach((el) => {
            const div = document.createElement('div');
            const tagName = el.tagName.toLowerCase();

            div.classList.add(tagName);

            if (el.className) {
              el.classList.forEach(cls => div.classList.add(cls));
            }

            // Strip typographic inline styles (font-family, color, etc.) pasted from Word/Google Docs
            const allowedStyleProps = new Set(['width', 'text-align', 'vertical-align', 'font-weight', 'background-color']);
            for (const attr of el.attributes) {
              if (attr.name === 'class' || attr.name === 'height') continue;
              if (attr.name === 'style') {
                const cleaned = attr.value
                  .split(';')
                  .map(s => s.trim())
                  .filter(s => {
                    if (!s) return false;
                    const prop = s.split(':')[0].trim().toLowerCase();
                    return allowedStyleProps.has(prop);
                  })
                  .join('; ');
                if (cleaned) div.setAttribute('style', cleaned);
              } else {
                div.setAttribute(attr.name, attr.value);
              }
            }

            while (el.firstChild) {
              div.appendChild(el.firstChild);
            }

            el.parentNode.replaceChild(div, el);
          });

          // Index figures so the List of Figures page can link to them
          const figures = tempDiv.querySelectorAll('figure');

          figures.forEach((figure) => {
            if (figure.closest('table')) return;

            const caption = figure.querySelector('figcaption');
            if (!caption) return;

            const captionText = (caption.innerText && caption.innerText !== 'Insert figure name here') ? caption.innerText : 'No caption provided';
            const figNum = figuresList.value.length + 1;
            const figId = `fig-${figNum}`;
            const figCaptionText = `Figure ${figNum}: ${captionText}`;

            figure.id = figId;
            caption.innerText = figCaptionText;

            figuresList.value.push({ id: figId, text: figCaptionText });
          });

          tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
            const cleanText = heading.textContent.replace(/ /g, '').trim();
            const hasMedia = heading.querySelector('img, table, figure, iframe');
            if (cleanText === '' && !hasMedia) {
              const p = document.createElement('p');
              p.innerHTML = '<br>';
              heading.parentNode.replaceChild(p, heading);
            }
          });

          const pTags = tempDiv.querySelectorAll('p');
          pTags.forEach((p) => {
            const cleanText = p.textContent.replace(/ /g, '').trim();
            const hasMedia = p.querySelector('img, table, figure, iframe, br');

            if (cleanText === '' && !hasMedia) {
              p.innerHTML = '<br>';
            }
          });

          item.value = tempDiv.innerHTML;
        }
      });
    }

    targetArray.push({
      ...section,
      level,
      rootId,
      parent,
      sectionNumber: index + 1,
    });

    if (Array.isArray(section.subsections) && section.subsections.length) {
      // eslint-disable-next-line no-unused-vars
      const { breakBefore, content, subsections, ...flatParent } = section;
      flattenSections(section.subsections, figuresList, level + 1, targetArray, (rootId ?? section.id), flatParent);
    }
  });

  return targetArray;
}
